import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Plus, Trash2, Edit2, Check, X, LayoutList, Grid2x2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * VariantsEditor lets admin create multiple matrix variants under a page.
 * Each variant becomes an independent config document (config_id =
 * `<parent>_var_<vid>`) with full layout customization (letters, positions,
 * sizes, colors, connections, cells, gates, etc.).
 *
 * Props:
 *  - token             — auth token
 *  - parentPage        — 'main' | 'student' | 'user_<slug>'
 *  - onEditVariant(cfgId)  — parent switches editing to this variant
 *  - variants, layout, onLayoutChange — controlled state from parent config
 */
const VariantsEditor = ({ token, parentPage, onEditVariant, onRefetchParent }) => {
  const [variants, setVariants] = useState([]);
  const [layout, setLayout] = useState('stacked');
  const [loading, setLoading] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [renamingId, setRenamingId] = useState(null);
  const [renameTitle, setRenameTitle] = useState('');
  const [flash, setFlash] = useState(null);

  const authCfg = { headers: { Authorization: `Bearer ${token}` }, params: { page: parentPage } };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/variants`, authCfg);
      setVariants(res.data.variants || []);
      setLayout(res.data.layout || 'stacked');
    } catch (e) {
      setFlash({ kind: 'err', msg: e.response?.data?.detail || 'Yüklənmədi' });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, parentPage]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!newTitle.trim()) return;
    try {
      await axios.post(`${API}/admin/variants`, { title: newTitle.trim() }, authCfg);
      setNewTitle('');
      await load();
      onRefetchParent && onRefetchParent();
      setFlash({ kind: 'ok', msg: 'Yeni variant əlavə olundu — parent config-in klonu ilə başlayır' });
    } catch (e) {
      setFlash({ kind: 'err', msg: e.response?.data?.detail || 'Yaradılmadı' });
    }
  };

  const rename = async (vid) => {
    try {
      await axios.put(`${API}/admin/variants/${vid}`, { title: renameTitle.trim() }, authCfg);
      setRenamingId(null);
      setRenameTitle('');
      await load();
      onRefetchParent && onRefetchParent();
    } catch (e) {
      setFlash({ kind: 'err', msg: e.response?.data?.detail || 'Yenilənmədi' });
    }
  };

  const setCompatEnabled = async (vid, enabled) => {
    // Optimistic UI so the checkbox flips immediately.
    setVariants((prev) => prev.map((v) => v.id === vid ? { ...v, compat_enabled: enabled } : v));
    try {
      await axios.put(`${API}/admin/variants/${vid}`, { compat_enabled: enabled }, authCfg);
      onRefetchParent && onRefetchParent();
    } catch (e) {
      // Revert on failure
      setVariants((prev) => prev.map((v) => v.id === vid ? { ...v, compat_enabled: !enabled } : v));
      setFlash({ kind: 'err', msg: e.response?.data?.detail || 'Yenilənmədi' });
    }
  };

  // Generic per-variant visibility toggle (hide_matrix / hide_grid / hide_groups).
  const setVisibilityFlag = async (vid, flag, value) => {
    setVariants((prev) => prev.map((v) => v.id === vid ? { ...v, [flag]: value } : v));
    try {
      await axios.put(`${API}/admin/variants/${vid}`, { [flag]: value }, authCfg);
      onRefetchParent && onRefetchParent();
    } catch (e) {
      setVariants((prev) => prev.map((v) => v.id === vid ? { ...v, [flag]: !value } : v));
      setFlash({ kind: 'err', msg: e.response?.data?.detail || 'Yenilənmədi' });
    }
  };

  const remove = async (v) => {
    if (!window.confirm(`Variant "${v.title}" silinsin? Config-i də silinəcək.`)) return;
    try {
      await axios.delete(`${API}/admin/variants/${v.id}`, authCfg);
      await load();
      onRefetchParent && onRefetchParent();
      setFlash({ kind: 'ok', msg: 'Variant silindi' });
    } catch (e) {
      setFlash({ kind: 'err', msg: e.response?.data?.detail || 'Silinmədi' });
    }
  };

  const setLayoutMode = async (mode) => {
    setLayout(mode);
    try {
      await axios.put(`${API}/admin/config`, { variants_layout: mode }, authCfg);
      onRefetchParent && onRefetchParent();
    } catch (e) {
      setFlash({ kind: 'err', msg: 'Layout dəyişilmədi' });
    }
  };

  return (
    <div className="admin-section">
      <h3 className="admin-section-title">Variantlar (çoxlu matriks diaqramları)</h3>
      <p className="admin-section-desc">
        Bu səhifədə əlavə matriks diaqramları yaradın — hər birinin öz hərfləri, yerləri, ölçüləri,
        rəngləri, formaları, bağlantıları, cədvəl sətirləri, hüceyrələri və s. tam müstəqil olaraq
        dəyişdirilə bilər. İstifadəçi eyni doğum tarixi ilə bütün variantları görəcək.
      </p>

      {flash && (
        <div style={{
          marginBottom: 12, padding: '10px 14px',
          background: flash.kind === 'ok' ? 'rgba(34,197,94,0.12)' : 'rgba(220,38,38,0.12)',
          border: `1px solid ${flash.kind === 'ok' ? 'rgba(34,197,94,0.4)' : 'rgba(220,38,38,0.4)'}`,
          color: flash.kind === 'ok' ? '#86efac' : '#fca5a5',
          borderRadius: 8, fontSize: 13, display: 'flex', justifyContent: 'space-between',
        }}>
          <span>{flash.msg}</span>
          <button onClick={() => setFlash(null)} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}>
            <X size={14} />
          </button>
        </div>
      )}

      {/* Layout mode toggle */}
      <div style={{ marginBottom: 16, padding: 12, background: 'rgba(168,85,247,0.06)', border: '1px solid rgba(168,85,247,0.2)', borderRadius: 8 }}>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', marginBottom: 8 }}>Public səhifədə göstərilmə üsulu:</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={() => setLayoutMode('stacked')}
            data-testid="variants-layout-stacked"
            style={{
              padding: '8px 14px', borderRadius: 8, fontSize: 12,
              background: layout === 'stacked' ? 'rgba(168,85,247,0.25)' : 'rgba(255,255,255,0.05)',
              border: `1px solid ${layout === 'stacked' ? 'rgba(168,85,247,0.6)' : 'rgba(255,255,255,0.15)'}`,
              color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
            }}>
            <LayoutList size={14} /> Aşağı-aşağı (stacked)
          </button>
          <button onClick={() => setLayoutMode('tabs')}
            data-testid="variants-layout-tabs"
            style={{
              padding: '8px 14px', borderRadius: 8, fontSize: 12,
              background: layout === 'tabs' ? 'rgba(168,85,247,0.25)' : 'rgba(255,255,255,0.05)',
              border: `1px solid ${layout === 'tabs' ? 'rgba(168,85,247,0.6)' : 'rgba(255,255,255,0.15)'}`,
              color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
            }}>
            <Grid2x2 size={14} /> Tab keçid
          </button>
        </div>
      </div>

      {/* Add new variant */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <input
          data-testid="variant-new-title"
          className="karma-input text-left"
          style={{ flex: 1, minWidth: 200 }}
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') create(); }}
          placeholder="Yeni variantın başlığı, məs: Uşaq matriksi"
        />
        <button onClick={create} disabled={!newTitle.trim()}
          data-testid="variant-add"
          className="admin-btn-primary text-xs px-4 py-2 inline-flex items-center gap-1 disabled:opacity-40">
          <Plus size={14} /> Əlavə et
        </button>
      </div>

      {/* Variants list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {loading && <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>Yüklənir…</div>}
        {!loading && variants.length === 0 && (
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', fontStyle: 'italic' }}>
            Hələ variant yoxdur. Yuxarıdan əlavə edin.
          </div>
        )}
        {variants.map((v) => (
          <div key={v.id} data-testid={`variant-row-${v.id}`}
            style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
              background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 8, flexWrap: 'wrap',
            }}>
            {renamingId === v.id ? (
              <>
                <input
                  className="karma-input text-left"
                  style={{ flex: 1, minWidth: 200 }}
                  value={renameTitle}
                  onChange={(e) => setRenameTitle(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') rename(v.id); }}
                  autoFocus
                />
                <button onClick={() => rename(v.id)} className="text-xs px-3 py-1.5 rounded border border-green-500/40 text-green-300 hover:bg-green-500/10">
                  <Check size={12} />
                </button>
                <button onClick={() => setRenamingId(null)} className="text-xs px-3 py-1.5 rounded border border-white/20 text-white/70">
                  <X size={12} />
                </button>
              </>
            ) : (
              <>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontSize: 14, color: 'white', fontWeight: 600 }}>{v.title}</div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', fontFamily: 'monospace' }}>
                    {parentPage}_var_{v.id}
                  </div>
                </div>
                <label
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '6px 10px', borderRadius: 8,
                    background: v.compat_enabled ? 'rgba(251,191,36,0.12)' : 'rgba(255,255,255,0.04)',
                    border: `1px solid ${v.compat_enabled ? 'rgba(251,191,36,0.45)' : 'rgba(255,255,255,0.12)'}`,
                    cursor: 'pointer', fontSize: 12,
                    color: v.compat_enabled ? '#fde68a' : 'rgba(255,255,255,0.7)',
                  }}
                  title="İl Enerji Uyumu (2-ci doğum tarixi) bu variantda göstərilsin"
                >
                  <input
                    type="checkbox"
                    checked={!!v.compat_enabled}
                    onChange={(e) => setCompatEnabled(v.id, e.target.checked)}
                    data-testid={`variant-compat-toggle-${v.id}`}
                    style={{ accentColor: '#fbbf24' }}
                  />
                  İl Enerji Uyumu
                </label>
                {[
                  { flag: 'hide_matrix', label: 'Matrisi gizlət', color: '#f87171' },
                  { flag: 'hide_grid', label: 'Grid gizlət', color: '#f87171' },
                  { flag: 'hide_groups', label: 'Qrupları gizlət', color: '#f87171' },
                ].map(({ flag, label, color }) => (
                  <label
                    key={flag}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '6px 10px', borderRadius: 8,
                      background: v[flag] ? 'rgba(248,113,113,0.12)' : 'rgba(255,255,255,0.04)',
                      border: `1px solid ${v[flag] ? 'rgba(248,113,113,0.45)' : 'rgba(255,255,255,0.12)'}`,
                      cursor: 'pointer', fontSize: 12,
                      color: v[flag] ? '#fca5a5' : 'rgba(255,255,255,0.7)',
                    }}
                    title={`${label} — variantda bu bölmə göstərilməyəcək`}
                  >
                    <input
                      type="checkbox"
                      checked={!!v[flag]}
                      onChange={(e) => setVisibilityFlag(v.id, flag, e.target.checked)}
                      data-testid={`variant-${flag.replace('_', '-')}-${v.id}`}
                      style={{ accentColor: color }}
                    />
                    {label}
                  </label>
                ))}
                <button
                  onClick={() => onEditVariant && onEditVariant(`${parentPage}_var_${v.id}`)}
                  data-testid={`variant-edit-${v.id}`}
                  className="text-xs px-3 py-1.5 rounded border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10">
                  Layout-u dəyiş
                </button>
                <button
                  onClick={() => { setRenamingId(v.id); setRenameTitle(v.title); }}
                  className="text-xs px-3 py-1.5 rounded border border-white/20 hover:bg-white/5 text-white/80">
                  <Edit2 size={12} />
                </button>
                <button onClick={() => remove(v)}
                  data-testid={`variant-delete-${v.id}`}
                  className="text-xs px-3 py-1.5 rounded border border-red-500/40 hover:bg-red-500/10 text-red-400">
                  <Trash2 size={12} />
                </button>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default VariantsEditor;
